import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Headset,
  MessageCircleQuestion,
  RotateCcw,
  SendHorizontal,
  Sparkles,
  X,
} from 'lucide-react'

import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

type ChatMessage = {
  role: 'user' | 'assistant'
  content: string
  error?: boolean
}

const MAX_CHARS = 800
const MAX_HISTORY = 12

const ERROR_TEXT: Record<string, string> = {
  rate_limited:
    "You've reached today's support message limit. Please try again tomorrow.",
  not_configured:
    'The support assistant is not configured yet. Please contact the admin.',
}
const GENERIC_ERROR =
  'The support assistant is unavailable right now. Please try again in a moment.'

const SUGGESTIONS = [
  'Check my last deposit',
  'My balance and tier',
  'How do I upgrade my tier?',
  'What is an emergency top-up?',
]

export function SupportChatWidget() {
  const { t, i18n } = useTranslation()
  const dir = i18n.dir()
  const isRtl = dir === 'rtl'

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [remaining, setRemaining] = useState<number | null>(null)

  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [messages, loading, open])

  useEffect(() => {
    if (!open) return
    const id = window.setTimeout(() => inputRef.current?.focus(), 200)
    if (remaining === null) {
      api
        .get('/api/support-agent/web-status')
        .then((res) => {
          if (res.data?.success) setRemaining(res.data.data.remaining)
        })
        .catch(() => {})
    }
    return () => window.clearTimeout(id)
  }, [open, remaining])

  // auto-grow textarea
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [input])

  const send = async (text: string) => {
    const content = text.trim()
    if (!content || loading || remaining === 0) return

    const next: ChatMessage[] = [...messages, { role: 'user', content }]
    setMessages(next)
    setInput('')
    setLoading(true)

    try {
      const history = next
        .filter((m) => !m.error)
        .slice(-MAX_HISTORY)
        .map(({ role, content }) => ({ role, content }))
      const res = await api.post('/api/support-agent/web-chat', {
        messages: history,
      })
      const body = res.data
      if (body?.success) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: body.data.reply },
        ])
        setRemaining(body.data.remaining)
      } else {
        if (body?.code === 'rate_limited') setRemaining(0)
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: t(ERROR_TEXT[body?.code] ?? GENERIC_ERROR),
            error: true,
          },
        ])
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t(GENERIC_ERROR), error: true },
      ])
    } finally {
      setLoading(false)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void send(input)
    }
  }

  const limitReached = remaining === 0

  return (
    <div dir={dir}>
      {/* ---------- Chat panel ---------- */}
      <div
        role='dialog'
        aria-label={t('Support assistant')}
        className={cn(
          'fixed end-4 bottom-24 z-50 flex flex-col overflow-hidden rounded-3xl border border-violet-200/60 bg-background shadow-2xl shadow-violet-500/20 transition-all duration-300 ease-out sm:end-6 dark:border-violet-500/20',
          'h-[min(620px,calc(100dvh-8rem))] w-[min(390px,calc(100vw-2rem))]',
          isRtl ? 'origin-bottom-left' : 'origin-bottom-right',
          open
            ? 'translate-y-0 scale-100 opacity-100'
            : 'pointer-events-none translate-y-4 scale-95 opacity-0'
        )}
      >
        {/* Header */}
        <div className='relative overflow-hidden bg-gradient-to-br from-violet-600 via-fuchsia-600 to-pink-500 px-4 py-4 text-white'>
          <div className='pointer-events-none absolute -end-10 -top-10 size-36 rounded-full bg-white/15 blur-2xl' />
          <div className='pointer-events-none absolute -bottom-12 start-10 size-28 rounded-full bg-amber-300/25 blur-2xl' />
          <div className='relative flex items-center gap-3'>
            <div className='relative flex size-11 items-center justify-center rounded-2xl bg-white/20 ring-1 ring-white/30 backdrop-blur'>
              <Headset className='size-5' />
              <span className='absolute -end-0.5 -bottom-0.5 size-3 rounded-full bg-emerald-400 ring-2 ring-fuchsia-600' />
            </div>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center gap-1.5 font-semibold'>
                {t('Support assistant')}
                <Sparkles className='size-3.5 text-amber-200' />
              </div>
              <div className='text-xs text-white/80'>
                {t('AI assistant · Online')}
              </div>
            </div>
            {messages.length > 0 && (
              <button
                type='button'
                onClick={() => setMessages([])}
                title={t('New conversation')}
                aria-label={t('New conversation')}
                className='rounded-xl p-2 text-white/85 transition hover:bg-white/15 hover:text-white'
              >
                <RotateCcw className='size-4' />
              </button>
            )}
            <button
              type='button'
              onClick={() => setOpen(false)}
              title={t('Close')}
              aria-label={t('Close')}
              className='rounded-xl p-2 text-white/85 transition hover:bg-white/15 hover:text-white'
            >
              <X className='size-4' />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className='flex-1 space-y-3 overflow-y-auto bg-gradient-to-b from-violet-50/60 to-background px-4 py-4 dark:from-violet-950/20'
        >
          {/* Welcome bubble (always first) */}
          <div className='flex items-end gap-2'>
            <div className='flex size-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-sm'>
              <Headset className='size-3.5' />
            </div>
            <div className='max-w-[85%] rounded-2xl rounded-es-md border border-violet-100 bg-white px-3.5 py-2.5 text-sm leading-relaxed shadow-sm dark:border-violet-500/20 dark:bg-muted'>
              {t(
                "Hi! I'm the Kia API AI support assistant (not a human). I can check your recent deposits, balance, tier and upgrade options. How can I help?"
              )}
            </div>
          </div>

          {messages.length === 0 && (
            <div className='space-y-2 pt-1'>
              <div className='flex items-center gap-1.5 px-1 text-xs font-medium text-muted-foreground'>
                <MessageCircleQuestion className='size-3.5' />
                {t('Quick questions')}
              </div>
              <div className='flex flex-wrap gap-2'>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type='button'
                    disabled={loading || limitReached}
                    onClick={() => void send(t(s))}
                    className='rounded-full border border-fuchsia-200 bg-white px-3 py-1.5 text-xs font-medium text-fuchsia-700 shadow-sm transition hover:-translate-y-0.5 hover:border-fuchsia-300 hover:bg-fuchsia-50 disabled:opacity-50 dark:border-fuchsia-500/30 dark:bg-fuchsia-500/10 dark:text-fuchsia-300'
                  >
                    {t(s)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) =>
            m.role === 'user' ? (
              <div key={i} className='flex justify-end'>
                <div
                  dir='auto'
                  className='max-w-[85%] rounded-2xl rounded-ee-md bg-gradient-to-br from-violet-600 to-fuchsia-600 px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap text-white shadow-md shadow-fuchsia-500/20'
                >
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={i} className='flex items-end gap-2'>
                <div
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-full text-white shadow-sm',
                    m.error
                      ? 'bg-rose-500'
                      : 'bg-gradient-to-br from-violet-500 to-fuchsia-500'
                  )}
                >
                  <Headset className='size-3.5' />
                </div>
                <div
                  dir='auto'
                  className={cn(
                    'max-w-[85%] rounded-2xl rounded-es-md border px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap shadow-sm',
                    m.error
                      ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300'
                      : 'border-violet-100 bg-white dark:border-violet-500/20 dark:bg-muted'
                  )}
                >
                  {m.content}
                </div>
              </div>
            )
          )}

          {loading && (
            <div className='flex items-end gap-2'>
              <div className='flex size-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-sm'>
                <Headset className='size-3.5' />
              </div>
              <div className='flex items-center gap-2 rounded-2xl rounded-es-md border border-violet-100 bg-white px-3.5 py-3 shadow-sm dark:border-violet-500/20 dark:bg-muted'>
                <span className='flex gap-1'>
                  <span className='size-1.5 animate-bounce rounded-full bg-violet-500 [animation-delay:-0.3s]' />
                  <span className='size-1.5 animate-bounce rounded-full bg-fuchsia-500 [animation-delay:-0.15s]' />
                  <span className='size-1.5 animate-bounce rounded-full bg-pink-500' />
                </span>
                <span className='text-xs text-muted-foreground'>
                  {t('Checking...')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className='border-t border-violet-100 bg-background px-3 pt-3 pb-2 dark:border-violet-500/20'>
          <div className='flex items-end gap-2 rounded-2xl border border-violet-200 bg-violet-50/40 p-1.5 transition focus-within:border-fuchsia-400 focus-within:ring-4 focus-within:ring-fuchsia-500/10 dark:border-violet-500/30 dark:bg-violet-500/5'>
            <textarea
              ref={inputRef}
              dir='auto'
              rows={1}
              value={input}
              maxLength={MAX_CHARS}
              disabled={limitReached}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                limitReached
                  ? t('Daily limit reached')
                  : t('Type your question...')
              }
              className='max-h-[120px] min-h-[36px] flex-1 resize-none bg-transparent px-2 py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed'
            />
            <button
              type='button'
              onClick={() => void send(input)}
              disabled={!input.trim() || loading || limitReached}
              title={t('Send')}
              aria-label={t('Send')}
              className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-md shadow-fuchsia-500/25 transition hover:scale-105 disabled:scale-100 disabled:opacity-40'
            >
              <SendHorizontal className={cn('size-4', isRtl && 'rotate-180')} />
            </button>
          </div>
          <div className='mt-1.5 flex items-center justify-between px-1 text-[11px] text-muted-foreground'>
            <span className='flex items-center gap-1'>
              <span className='size-1.5 rounded-full bg-emerald-500' />
              {t('Free · does not use your balance')}
            </span>
            {remaining !== null && (
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 font-medium',
                  remaining <= 5
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                    : 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300'
                )}
              >
                {t('{{count}} messages left today', { count: remaining })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ---------- Launcher button ---------- */}
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t('Close') : t('Open support chat')}
        title={open ? t('Close') : t('Open support chat')}
        className='group fixed end-4 bottom-5 z-50 flex size-14 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 via-fuchsia-600 to-pink-500 text-white shadow-xl shadow-fuchsia-500/30 transition hover:scale-105 active:scale-95 sm:end-6'
      >
        {!open && messages.length === 0 && (
          <span className='absolute inset-0 animate-ping rounded-full bg-fuchsia-500/40 [animation-duration:2.5s]' />
        )}
        <span className='relative'>
          {open ? (
            <X className='size-6 transition group-hover:rotate-90' />
          ) : (
            <Headset className='size-6' />
          )}
        </span>
      </button>
    </div>
  )
}
